// OwnerSyncOpsTable — one engagement's AppFolio writes on the Owners page
// (owner-sync spec §7): what each step is, whose it is, where it stands, and —
// for a failed or stuck step — Retry, Mark resolved, and (for a stuck step
// whose outcome is unknown, where Retry would only replay the recorded answer)
// "Not in AppFolio — send again". Each button shows only when its flag is set.
// A property attachment waiting for its AppFolio property (the signed City
// utilities agreement) also offers "Link the AppFolio property".
//
// The op key addresses the buttons and nothing else; the router's raw error
// message is never shown (it can carry id fragments), only a plain-language
// hint from the error CODE (lib/ownerSync.ts syncErrorHint).

import {
  LINK_PROPERTY_LABEL,
  RESEND_CONFIRMED_LABEL,
  syncErrorHint,
  syncStatusTone,
  type OwnerSyncOpView,
} from '@/lib/ownerSync';

export function OwnerSyncOpsTable({
  ops,
  busyOpKey,
  onRetry,
  onResolve,
  onResendConfirmed,
  onLinkProperty,
  formatWhen,
}: {
  ops: OwnerSyncOpView[];
  /** The op whose Retry / Mark resolved is in flight (its buttons disable). */
  busyOpKey?: string | null | undefined;
  onRetry: (op: OwnerSyncOpView) => void;
  onResolve: (op: OwnerSyncOpView) => void;
  /** Opens the "send this step again?" confirm — only offered when canResendConfirmed. */
  onResendConfirmed?: ((op: OwnerSyncOpView) => void) | undefined;
  /** Opens the property picker — only offered when canLinkProperty. */
  onLinkProperty?: ((op: OwnerSyncOpView) => void) | undefined;
  formatWhen: (iso: string) => string;
}): React.JSX.Element | null {
  if (ops.length === 0) return null;
  return (
    <div className="oc-table-wrap">
      <table className="oc-table" aria-label="AppFolio sync">
        <thead>
          <tr>
            <th scope="col">Step</th>
            <th scope="col">Owner</th>
            <th scope="col">Status</th>
            <th scope="col">Updated</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {ops.map((op) => {
            const hint = syncErrorHint(op);
            const busy = busyOpKey === op.opKey;
            const canLink = !!op.canLinkProperty && !!onLinkProperty;
            return (
              <tr key={op.opKey}>
                <td>
                  {op.label}
                  {hint && <div className="oc-cell-sub">{hint}</div>}
                </td>
                <td>{op.ownerName ?? '—'}</td>
                <td>
                  <span className="pill" data-tone={syncStatusTone(op.status)} data-size="sm">
                    {op.statusLabel}
                  </span>
                </td>
                <td>{formatWhen(op.updatedAt)}</td>
                <td>
                  {op.canRetry || op.canResolve || canLink || (op.canResendConfirmed && onResendConfirmed) ? (
                    <span style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {canLink && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          disabled={busy}
                          onClick={() => onLinkProperty!(op)}
                        >
                          {LINK_PROPERTY_LABEL}
                        </button>
                      )}
                      {op.canRetry && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          disabled={busy}
                          onClick={() => onRetry(op)}
                        >
                          {busy ? 'Working…' : 'Retry'}
                        </button>
                      )}
                      {op.canResendConfirmed && onResendConfirmed && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          disabled={busy}
                          onClick={() => onResendConfirmed(op)}
                        >
                          {RESEND_CONFIRMED_LABEL}
                        </button>
                      )}
                      {op.canResolve && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="ghost"
                          data-size="sm"
                          disabled={busy}
                          onClick={() => onResolve(op)}
                        >
                          Mark resolved
                        </button>
                      )}
                    </span>
                  ) : (
                    <span className="oc-meta">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
